import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import MainDrawer from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import ConsumerGroupTable from "./ConsumerGroupTable";
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const ConsumerGroupPage: React.FC = () => {
    const [consumerGroups, setConsumerGroups] = useState([]);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);

    const [isLoading, setIsLoading] = useState(true);
    
    // ## 1. ADDED new fields to the column filters state
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>({
        name: true,
        owner: true,
        rate: true,
        burst: true,
        quotaPeriod: true,
        createdAt: true,
    });
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const toggleColumnFilter = (column: string) => {
        setColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    React.useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true); 
            try {
                // ## 2. ADDED rate, burst, and quotaPeriod to the GraphQL query
                const data = await queryGraphql(`
                    query GetConsumerGroups {
                      getConsumerGroups {
                        id
                        name
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        rate
                        burst
                        quotaPeriod
                        consumerGroupOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        consumerGroupApprover {
                            id
                            name
                        }
                      }
                    }
                `);

                let consumerGroupsArr = data.getConsumerGroups.map((cg: any) => ({
                    ...cg,
                    owner: cg?.consumerGroupOwner?.name,
                    createdAt: new Date(Number(cg.createdAt)).toISOString(),
                    ownerAvatar: cg?.consumerGroupOwner?.googleProfilePicture,
                }));

                setConsumerGroups(consumerGroupsArr);

                searchOptions = consumerGroupsArr
                    .map((cg: any) => [
                        {
                            category: "name",
                            groupBy: "CONSUMER GROUP NAME",
                            chipLabel: `Name: ${cg.name}`,
                            dropdownLabel: cg.name,
                        },
                        {
                            category: "owner",
                            groupBy: "OWNER",
                            chipLabel: `Owner: ${cg.owner}`,
                            dropdownLabel: cg.owner,
                        },
                    ])
                    .flat();

                // Remove duplicates and sort
                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false); 
            }
        };

        fetchData();
    }, []);

    const filteredConsumerGroups =
        searchQuery.length === 0
            ? consumerGroups
            : consumerGroups.filter((cg: any) =>
                Object.entries({
                    name: cg.name,
                    owner: cg.owner,
                }).every(([category, value]) => {
                    const filters = searchQuery.filter((e) => e.category === category);
                    if (filters.length === 0) return true;
                    // Ensure value is not null or undefined before calling toLowerCase
                    return value && filters.map((e) => e.dropdownLabel.toLowerCase()).includes(value.toLowerCase());
                })
            );

    const totalConsumerGroupsOverall = consumerGroups.length;
    const totalMatchingSearch = filteredConsumerGroups.length;
    const filteredActiveConsumerGroups = filteredConsumerGroups.filter((cg: any) => cg.status === "active");
    const filteredPendingConsumerGroups = filteredConsumerGroups.filter((cg: any) => cg.status === "pending");
    const filteredRejectedConsumerGroups = filteredConsumerGroups.filter((cg: any) => cg.status === "rejected");

    return (
        <Box sx={{ display: "flex" }}>
            <MiniBot />
            <MainDrawer index={"apis-consumer-group"} />
            <Box component="main" sx={{ flex: 1, bgcolor: "#fafafa", minHeight: window.innerHeight }}>
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 1,
                                mx: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Consumer Groups
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Button
                                    variant="contained"
                                    disabled={!user.roles?.some((e: string) => ["developer", "viewer"].includes(e))}
                                    sx={{
                                        backgroundColor: "black",
                                        color: "white",
                                        padding: "8px 24px",
                                        fontSize: "0.875rem",
                                        textTransform: "none",
                                        '&:hover': {
                                            backgroundColor: '#333',
                                        }
                                    }}
                                    onClick={() => navigate("/consumer-group/add")}
                                >
                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">
                                        Add New Consumer Group
                                    </Typography>
                                </Button>
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{ padding: 0, border: "none", ml: 2, "&:hover": { backgroundColor: "action.hover" } }}
                                >
                                    <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40, height: 40, display: "block" }} />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by name, owner etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px", paddingLeft: "4px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                            <Button
                                sx={{ marginLeft: "16px", border: "1px solid #bbb", borderRadius: "10px", height: "45px", width: "45px" }}
                                onClick={handleFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            >
                                <Box sx={{ padding: "8px" }}>
                                    <Typography sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.keys(columnFilters).map((column) => (
                                            <FormControlLabel
                                                key={column}
                                                control={
                                                    <Checkbox
                                                        checked={columnFilters[column as keyof typeof columnFilters]}
                                                        onChange={() => toggleColumnFilter(column)}
                                                        sx={{ color: "#000", "&.Mui-checked": { color: "#000" }, "& .MuiSvgIcon-root": { borderRadius: "50%" } }}
                                                    />
                                                }
                                                label={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, " $1").trim()}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button variant="contained" onClick={handleFilterClose} sx={{ marginTop: "8px", backgroundColor: "black", color: "white", width: "100%" }}>
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>
                    <Box m={2}>
                        {totalConsumerGroupsOverall > 0 && totalMatchingSearch > 0 && (
                            <>
                        {filteredPendingConsumerGroups.length > 0 && (
                            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Pending Consumer Groups ({filteredPendingConsumerGroups.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ConsumerGroupTable consumerGroups={filteredPendingConsumerGroups} columnFilters={columnFilters} type="pending" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {filteredActiveConsumerGroups.length > 0 && (
                            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Active Consumer Groups ({filteredActiveConsumerGroups.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ConsumerGroupTable consumerGroups={filteredActiveConsumerGroups} columnFilters={columnFilters} type="active" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {filteredRejectedConsumerGroups.length > 0 && (
                            <Accordion elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                            Rejected Consumer Groups ({filteredRejectedConsumerGroups.length})
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <ConsumerGroupTable consumerGroups={filteredRejectedConsumerGroups} columnFilters={columnFilters} type="rejected" />
                                </AccordionDetails>
                            </Accordion>
                        )}
                        </>)}

                        {totalConsumerGroupsOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Matches" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No consumer groups match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalConsumerGroupsOverall === 0 && !isLoading && (
                            <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Content" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No consumer groups are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first consumer group.
                                </Typography>
                            </Box>
                        )}
                        
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default ConsumerGroupPage;